import type {
  ReportTemplate,
  ReportTemplateDefaults,
} from 'api/analytics-reports/types';
import { isReportContentPostsEnabled } from 'app/modules/analytics/modules/reports/constants/reportContentPostSettings';
import type { ReportContentDefaults } from 'app/modules/analytics/modules/reports/utils/getReportContentDefaults';
import { pageTypeToAnalyticsPlatform } from 'app/modules/analytics/modules/content/utils/analyticsPlatformToPageType';
import type { PageData, ProfileGroup } from 'types/Page';

export interface ReportProfileEditorState {
  clientId: string;
  type: 'general' | 'profile';
  pageId?: number;
  pageName?: string;
  pageLogoSrc?: string;
  pageType?: number;
  platform?: string;
  orderColumn: number;
  isCollapsed?: boolean;
  performanceMetrics: string[];
  contentMetrics: string[];
  performanceNote: string;
  contentNote: string;
  contentDefaults: ReportContentDefaults;
}

export function contentDefaultsFromTemplateDefaults(
  defaults: ReportTemplateDefaults,
): ReportContentDefaults {
  return {
    content_order_by_posts: defaults.content_order_by_posts ?? null,
    content_order_by_reels: defaults.content_order_by_reels ?? null,
    content_post_count: defaults.content_post_count ?? null,
  };
}

interface ProfileCardPageInput {
  id: number;
  name: string;
  logoSrc?: string;
  type: number;
}

export function createEmptyProfileCard(
  type: 'general' | 'profile',
  orderColumn: number,
  page?: ProfileCardPageInput,
): ReportProfileEditorState {
  return {
    clientId: type === 'general' ? 'general' : `profile-${page!.id}`,
    type,
    pageId: page?.id,
    pageName: page?.name,
    pageLogoSrc: page?.logoSrc,
    pageType: page?.type,
    platform: page
      ? pageTypeToAnalyticsPlatform(page.type) ?? undefined
      : undefined,
    orderColumn,
    isCollapsed: false,
    performanceMetrics: [],
    contentMetrics: [],
    performanceNote: '',
    contentNote: '',
    contentDefaults: {
      content_order_by_posts: null,
      content_order_by_reels: null,
      content_post_count: null,
    },
  };
}

export function seedProfileCardsFromSelection(
  isGroupSelected: boolean,
  selectedProfileGroup: ProfileGroup | null,
  selectedPage: PageData | null,
): ReportProfileEditorState[] {
  if (isGroupSelected && selectedProfileGroup) {
    const cards: ReportProfileEditorState[] = [
      createEmptyProfileCard('general', 0),
    ];

    selectedProfileGroup.pages.forEach((page, index) => {
      cards.push(
        createEmptyProfileCard('profile', index + 1, {
          id: page.id,
          name: page.name ?? '',
          logoSrc: page.logo?.src,
          type: page.type,
        }),
      );
    });

    return cards;
  }

  if (selectedPage) {
    return [
      createEmptyProfileCard('profile', 0, {
        id: selectedPage.id,
        name: selectedPage.name ?? '',
        logoSrc: selectedPage.logo?.src,
        type: selectedPage.type,
      }),
    ];
  }

  return [];
}

export function seedProfileCardsForCreateModal(
  isGroupSelected: boolean,
  selectedProfileGroup: ProfileGroup | null,
  selectedPage: PageData | null,
): ReportProfileEditorState[] {
  if (isGroupSelected && selectedProfileGroup) {
    const cards: ReportProfileEditorState[] = [];

    selectedProfileGroup.pages.forEach((page, index) => {
      cards.push(
        createEmptyProfileCard('profile', index, {
          id: page.id,
          name: page.name ?? '',
          logoSrc: page.logo?.src,
          type: page.type,
        }),
      );
    });

    return cards;
  }

  if (selectedPage) {
    return [
      createEmptyProfileCard('profile', 0, {
        id: selectedPage.id,
        name: selectedPage.name ?? '',
        logoSrc: selectedPage.logo?.src,
        type: selectedPage.type,
      }),
    ];
  }

  return [];
}

export function applyTemplateToProfileCards(
  cards: ReportProfileEditorState[],
  template: ReportTemplate,
): ReportProfileEditorState[] {
  return cards.map((card) => {
    const defaults =
      card.type === 'general'
        ? template.general_defaults
        : template.profile_defaults;

    return {
      ...card,
      performanceMetrics: [...defaults.performance_metrics],
      contentMetrics: [...defaults.content_metrics],
      performanceNote: '',
      contentNote: '',
      contentDefaults: contentDefaultsFromTemplateDefaults(defaults),
    };
  });
}

export function buildProfileCardsForCreateReport(
  isGroupSelected: boolean,
  selectedProfileGroup: ProfileGroup | null,
  selectedPage: PageData | null,
  template: ReportTemplate | null,
): ReportProfileEditorState[] {
  const seededCards = seedProfileCardsFromSelection(
    isGroupSelected,
    selectedProfileGroup,
    selectedPage,
  );

  if (!template) {
    return seededCards;
  }

  return applyTemplateToProfileCards(seededCards, template);
}

export function normalizeProfileCardOrder(
  cards: ReportProfileEditorState[],
): ReportProfileEditorState[] {
  return sortedProfileCards(cards).map((card, index) => ({
    ...card,
    orderColumn: index,
  }));
}

export function sortedProfileCards(
  cards: ReportProfileEditorState[],
): ReportProfileEditorState[] {
  return cards
    .slice()
    .sort((left, right) => left.orderColumn - right.orderColumn);
}

export function hasAnyProfileCardMetrics(
  cards: ReportProfileEditorState[],
): boolean {
  return cards.some(
    (card) =>
      card.performanceMetrics.length > 0 ||
      card.contentMetrics.length > 0 ||
      isReportContentPostsEnabled(card.contentDefaults),
  );
}

export interface ReportSectionMetricsContext {
  template: ReportTemplate | null;
  availablePerformanceMetrics: string[];
  availableContentMetrics: string[];
}

export function resolveCardSectionMetrics(
  card: ReportProfileEditorState,
  kind: 'performance' | 'content',
): string[] {
  return kind === 'performance' ? card.performanceMetrics : card.contentMetrics;
}

export function profileCardHasRequiredSections(
  card: ReportProfileEditorState,
): boolean {
  const hasPerformanceMetrics = card.performanceMetrics.length > 0;
  const hasContentMetrics =
    card.contentMetrics.length > 0 ||
    isReportContentPostsEnabled(card.contentDefaults);

  return hasPerformanceMetrics || hasContentMetrics;
}

export function everyProfileCardHasRequiredSections(
  cards: ReportProfileEditorState[],
): boolean {
  return (
    cards.length > 0 &&
    cards.every((card) => profileCardHasRequiredSections(card))
  );
}

export function hasAnyProfileCardNotes(
  cards: ReportProfileEditorState[],
): boolean {
  return cards.some(
    (card) =>
      card.performanceNote.trim() !== '' || card.contentNote.trim() !== '',
  );
}

function arraysEqual(left: string[], right: string[]): boolean {
  if (left.length !== right.length) {
    return false;
  }

  return left.every((value, index) => value === right[index]);
}

function contentDefaultsEqual(
  left: ReportProfileEditorState['contentDefaults'],
  right: ReportContentDefaults,
): boolean {
  return (
    left.content_order_by_posts === right.content_order_by_posts &&
    left.content_order_by_reels === right.content_order_by_reels &&
    left.content_post_count === right.content_post_count
  );
}

export function profileCardsDifferFromTemplate(
  cards: ReportProfileEditorState[],
  template: ReportTemplate,
): boolean {
  return cards.some((card) => {
    const defaults =
      card.type === 'general'
        ? template.general_defaults
        : template.profile_defaults;

    const templateContentDefaults =
      contentDefaultsFromTemplateDefaults(defaults);

    return (
      !arraysEqual(card.performanceMetrics, defaults.performance_metrics) ||
      !arraysEqual(card.contentMetrics, defaults.content_metrics) ||
      !contentDefaultsEqual(card.contentDefaults, templateContentDefaults)
    );
  });
}
